import { useCallback, useId, type ReactNode } from "react";
import { oMark } from "../../brand.ts";
import { useAuth } from "../../auth/index.ts";
import "./login.css";

/**
 * The locked **"Access Requested"** screen (per-team-signin-factors task 14; Requirements 9.2, 9.3,
 * 9.7).
 *
 * A pending-only session — an authenticated user whose only non-banned membership(s) are
 * `pending_approval`, so the server minted a session but resolved NO active team (worker tasks 6/8) —
 * is routed HERE and nowhere else. It is a deliberate change from the old pre-login
 * `/login?sso=pending` notice (R9.1): the user is now signed in, but every other authenticated view
 * and every team-scoped API is denied (the server answers team-scoped `/api/*` with a `403
 * pending_approval`, worker task 8), so the SPA locks the whole session onto this screen until a team
 * admin approves the request.
 *
 * The copy is the SAME approval notice the login page already shows for the SSO `?sso=pending`
 * redirect (R9.2) — "awaiting administrator approval … contact your organization's administrator" —
 * now presented as a logged-in locked screen rather than a pre-login message. The only action is to
 * sign out (so the user is not trapped), which clears the session and returns to `/login`.
 *
 * It is purely presentational beyond reading {@link useAuth} for the display email and `signOut`; the
 * route decision (when to render this screen) lives in the SPA router guard (`app/App.tsx`), so this
 * component just renders the locked state it is handed.
 */

/** The approval-notice copy, reused verbatim from the login page's `?sso=pending` notice (R9.2). */
export const ACCESS_REQUESTED_MESSAGE =
  "Your account was created and is awaiting administrator approval. Sign-in can\u2019t be " +
  "completed until an administrator approves your request. Please contact your " +
  "organization\u2019s administrator to expedite approval.";

export function AccessRequestedScreen(): ReactNode {
  const auth = useAuth();
  const headingId = useId();
  const noticeId = useId();

  const onSignOut = useCallback(() => {
    void auth.signOut();
  }, [auth]);

  const email = auth.identity?.user.email ?? "";

  return (
    <div className="oa-auth oa-login">
      <main className="oa-login__panel" aria-labelledby={headingId}>
        <div className="oa-login__card">
          <div className="oa-login__badge">
            <img src={oMark} alt="OATHERA" className="oa-login__mark" width={40} height={40} />
          </div>

          <h1 id={headingId} className="oa-login__heading">
            Access requested
          </h1>
          <p className="oa-login__subheading">
            {email.length > 0
              ? `You are signed in as ${email}.`
              : "You are signed in."}
          </p>

          <p id={noticeId} className="oa-login__notice" role="status" aria-live="polite">
            {ACCESS_REQUESTED_MESSAGE}
          </p>

          <button
            type="button"
            className="oa-login__submit"
            onClick={onSignOut}
            disabled={auth.signingOut}
          >
            {auth.signingOut ? "Signing out\u2026" : "Sign out"}
          </button>
        </div>
      </main>
    </div>
  );
}
